import { action, computed, makeObservable, observable } from 'mobx';
import flatten from 'lodash/flatten';
import compact from 'lodash/compact';
import { v4 as uuid } from 'uuid';

import {
    EntryTypeBlockData,
    EntryTypeCreateForm,
    EntryTypeData,
    EntryTypeSectionBlockSettingsData,
    EntryTypeSectionData,
    Nullable,
    Optional,
    Sex,
    TableFieldCellType,
} from 'chaika-frontend-types/frontend-cms';
import { TranslationsDataParams } from 'chaika-types';
import { EntryTypeFieldType, EntryTypeStatus, EntryTypeType, Locale } from 'chaika-enums';

import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { changePositionInMapObject } from '@utils/changePositionInMapObject';
import { assertIsDefined } from '@utils/assertIsDefined';

import { EntryTypeSection } from './EntryTypeSection';
import { EntryTypeBlock } from './EntryTypeBlock';
import { EntryTypeField, FieldData } from './EntryTypeField';
import { OptionType } from '@components/Select';
import { entryTypeIdsWithHideAuthor, TABLE_CELL_NAME_SEPARATOR } from '@store/types';

export class EntryType {
    @observable public id!: string;
    @observable public version!: Nullable<number>;
    @observable public type!: EntryTypeType;
    @observable public title!: string;
    @observable public isNested!: boolean;
    @observable public isDefault!: boolean;
    @observable public status!: Nullable<EntryTypeStatus>;
    @observable public creationTime!: string;
    @observable public titleTranslations = new Map<Locale, TranslationsDataParams>();
    @observable public legalEntityIds?: string[];
    @observable public data: EntryTypeData;
    @observable private sections_: Map<string, EntryTypeSection> = new Map();
    private fieldTypes = new Set([EntryTypeFieldType.Text, EntryTypeFieldType.String, EntryTypeFieldType.Table]);

    constructor(params: EntryTypeData = {} as EntryTypeData) {
        makeObservable(this);
        this.data = params;
        this.setEntryType(params);
    }

    @computed
    public get hasNestedEntryField(): boolean {
        return true;
    }

    @computed
    public get isSystem(): boolean {
        return this.type === EntryTypeType.System;
    }

    @computed
    public get saveParams(): EntryTypeCreateForm {
        const sections = this.sections.map(section => section.saveParams);

        return {
            title: this.title,
            type: this.type,
            legalEntityIds: this.legalEntityIds,
            titleTranslations: this.titleTranslationsValues,
            isNested: this.isNested,
            isDefault: this.isDefault,
            sections: sections.filter(section => section.blocks.length > 0),
        };
    }

    @computed
    public get sections(): EntryTypeSection[] {
        return Array.from(this.sections_.values());
    }

    @computed
    public get sectionsWithData(): EntryTypeSection[] {
        return this.sections.filter(section => section.hasData);
    }

    @computed
    public get sectionsWithDataAndAlwaysShow(): EntryTypeSection[] {
        return this.sections.filter(section => section.hasDataAndAlwaysShow);
    }

    @computed
    public get maleSections(): EntryTypeSection[] {
        return Array.from(this.sections_.values()).filter(({ settings }) => settings?.onlyFemale !== true);
    }

    @computed
    public get sectionKeys(): string[] {
        return Array.from(this.sections_.keys());
    }

    @computed
    public get fields(): EntryTypeField[] {
        const fields: EntryTypeField[][] = [];

        this.sections_.forEach(sections => {
            fields.push(sections.fields);
        });

        return flatten(fields);
    }

    @computed
    public get tableFields(): EntryTypeField[] {
        const fields: EntryTypeField[][] = [];

        this.sections_.forEach(sections => {
            fields.push(sections.fields);
        });

        return flatten(fields).filter(field => field.type === EntryTypeFieldType.Table);
    }

    @computed
    public get blocks(): EntryTypeBlock[] {
        const blocks: EntryTypeBlock[][] = [];

        this.sections_.forEach(sections => {
            blocks.push(sections.blocks);
        });

        return flatten(blocks);
    }

    @computed
    public get blockIds(): string[] {
        return this.blocks.map(block => block.id);
    }

    @computed
    public get translationLocales(): string[] {
        return Array.from(this.titleTranslations.keys());
    }

    @computed
    public get titleTranslationsValues(): TranslationsDataParams[] {
        return toArrayFromIterable(this.titleTranslations);
    }

    @computed
    public get titleTranslationsEntry(): Map<Locale, TranslationsDataParams> {
        return this.titleTranslations;
    }

    @computed
    public get removeFields(): Map<Locale, TranslationsDataParams> {
        return this.titleTranslations;
    }

    @action
    public setEntryType(
        {
            id = '',
            title = '',
            status,
            type,
            isNested = false,
            isDefault = false,
            creationTime = '',
            sections = [],
            version,
            titleTranslations = [],
            legalEntityIds = [],
        }: EntryTypeData = {} as EntryTypeData,
    ) {
        this.id = id;
        this.title = title;
        this.type = type;
        this.status = status;
        this.isNested = isNested;
        this.isDefault = isDefault;
        this.creationTime = creationTime;
        this.version = version;
        this.legalEntityIds = legalEntityIds;
        titleTranslations.forEach(title => this.titleTranslations.set(title.locale, title));

        this.setSections(sections);
    }

    @action
    public setSections(sections: EntryTypeSectionData[]): void {
        this.sections_.clear();

        sections.forEach(section => {
            const id = section?.id ?? uuid();
            this.sections_.set(id, new EntryTypeSection({ ...section, id }));
        });
    }

    @action
    public setEntryTitleTranslations(params: TranslationsDataParams): void {
        this.titleTranslations.set(params.locale, params);
    }

    @action
    public reset(): void {
        this.setEntryType();
    }

    public getFieldsForTranslationByBlocks(sex: Sex) {
        const sections = this.getSectionsBySex(sex);
        const blocksGroupedBySection = compact(
            flatten(
                sections.map(section => ({
                    sectionId: section.id,
                    blocks: section?.blocks,
                })),
            ),
        );

        return compact(
            flatten(
                blocksGroupedBySection.map(group =>
                    group.blocks
                        ?.map(block => {
                            return {
                                blockTitle: this.getBlock(group.sectionId, block.id)?.title ?? '',
                                fields: compact(
                                    block.fields
                                        .map(field => this.getField(group.sectionId, block.id, field.name))
                                        .filter(field => field?.hasTranslationMode)
                                        .filter(field => (sex === Sex.Female ? field : !field?.data.onlyFemale)),
                                ),
                            };
                        })
                        .filter(block => block.fields.length > 0),
                ),
            ),
        );
    }

    public getSectionsBySex(sex: Sex): EntryTypeSection[] {
        return sex === Sex.Male ? this.maleSections : this.sections;
    }

    public getSection(id: string): Optional<EntryTypeSection> {
        return this.sections_.get(id);
    }

    public getSectionPosition(sectionId: string): number {
        return this.sectionKeys.findIndex(id => id === sectionId);
    }

    @action
    public changeSectionPosition(oldPosition: number, newPosition: number) {
        this.sections_ = changePositionInMapObject(this.sections_, oldPosition, newPosition);
    }

    public isHideAuthorForEntryType(): boolean {
        return entryTypeIdsWithHideAuthor.has(this.id);
    }

    public getBlockPosition(blockId: string): Optional<number> {
        const sectionWithBlock = this.sections.find(section => section.blockIds.includes(blockId));

        return sectionWithBlock?.blockIds.findIndex(id => id === blockId);
    }

    public getBlockSettings(blockId: string): Optional<EntryTypeSectionBlockSettingsData> {
        const sectionWithBlock = this.sections.find(section => section.blockIds.includes(blockId));

        return sectionWithBlock?.getBlockSettings(blockId);
    }

    public getBlockWidth(blockId: string): Optional<number> {
        const sectionWithBlock = this.sections.find(section => section.blockIds.includes(blockId));

        return sectionWithBlock?.getBlockWidth(blockId);
    }

    public getBlockAlign(blockId: string): Optional<string> {
        const sectionWithBlock = this.sections.find(section => section.blockIds.includes(blockId));

        return sectionWithBlock?.getBlockAlign(blockId);
    }

    @action
    public changeBlockPosition(blockId: string, oldPosition: number, newPosition: number) {
        const sectionWithBlock = this.sections.find(section => section.blockIds.includes(blockId));

        sectionWithBlock?.changeBlockPosition(oldPosition, newPosition);
    }

    @action
    public updateBlockSettings(blockId: string, params: Partial<EntryTypeSectionBlockSettingsData>): void {
        const sectionWithBlock = this.sections.find(section => section.blockIds.includes(blockId));

        sectionWithBlock?.updateBlockSettings(blockId, params);
    }

    @action
    public addSection(params: Partial<EntryTypeSectionData> & { title: string }): void {
        const id = params.id ?? uuid();
        this.sections_.set(id, new EntryTypeSection({ id, collapsible: false, blocks: [], ...params }));
    }

    @action
    public removeSection(title: string): void {
        this.sections_.delete(title);
    }

    @action
    public removeBlock(sectionTitle: string, blockId: string): void {
        const section = this.sections_.get(sectionTitle);

        section?.removeBlock(blockId);
    }

    @action
    public addBlock(sectionId: string, block: EntryTypeBlockData): void {
        const section = this.sections_.get(sectionId);

        section?.addBlock(block);
    }

    public getBlock(sectionId: string, blockId: string): Optional<EntryTypeBlock> {
        const section = this.getSection(sectionId);

        return section?.getBlock(blockId);
    }

    public getField(sectionId: string, blockId: string, fieldName: string): Optional<EntryTypeField> {
        const block = this.getBlock(sectionId, blockId);

        return block?.getField(fieldName);
    }

    public getFieldByName(name: string): Optional<EntryTypeField> {
        return this.fields.find(item => item.name === name);
    }

    public getFieldBlockTitleByName(name: string): Optional<string> {
        const block = this.blocks.find(block => block.hasField(name));
        return block?.title;
    }

    public makeFieldsOptions(customFieldTypes?: EntryTypeFieldType[]): OptionType<string>[] {
        const fieldTypes = customFieldTypes ? new Set(customFieldTypes) : this.fieldTypes;
        return flatten(
            this.fields.filter(({ type }) => fieldTypes.has(type)).map(field => this.makeOptionsFromField(field)),
        );
    }

    public makeOptionsFromField({ name, title, type, data }: EntryTypeField): OptionType<string>[] {
        return type === EntryTypeFieldType.Table
            ? this.getTableCellOptions(name, title, data)
            : [this.getTextFieldOption(name, title)];
    }

    private getTextFieldOption(name: string, title?: string): OptionType<string> {
        const parts = compact([this.getFieldBlockTitleByName(name) ?? name, title]);

        return { value: name, label: parts.join(' — ') };
    }

    private getTableCellOptions(name: string, title?: string, data?: FieldData): OptionType<string>[] {
        const blockName = this.getFieldBlockTitleByName(name) ?? name;
        const table = data?.table;
        assertIsDefined(table, `Table field ${name} must have table data`);

        return flatten(table.cells)
            .filter(cell => cell.type !== TableFieldCellType.Title)
            .map(cell => {
                const parts = compact([blockName, title, cell.name]);

                return {
                    value: `${name}${TABLE_CELL_NAME_SEPARATOR}${cell.name}`,
                    label: parts.join(' — '),
                };
            });
    }
}
